/**
 * Sentry event scrubbing for URL-borne secrets.
 *
 * `sendDefaultPii: false` does NOT strip query strings, and several
 * flows carry secrets in the URL: `/reset-password?token=…&email=…`,
 * `/verify-email/<token>`, and event check-in `?token=…`. Any error or
 * sampled transaction on those pages would otherwise ship a live
 * credential to Sentry. Applied via beforeSend/beforeSendTransaction in
 * all three Sentry configs (client, server, edge).
 */

const QUERY_PARAM_RE = /([?&])(token|email)=[^&#]*/gi;
const TOKEN_PATH_RE = /\/(verify-email)\/[^/?#]+/g;

/** Redact sensitive query params and token-bearing path segments. */
export function scrubUrl(url: string): string {
  return url
    .replace(TOKEN_PATH_RE, '/$1/[redacted]')
    .replace(QUERY_PARAM_RE, '$1$2=[redacted]');
}

function scrubQueryString(qs: string): string {
  return qs.replace(/(^|&)(token|email)=[^&#]*/gi, '$1$2=[redacted]');
}

// Structural type: covers the fields we touch on both error and
// transaction events without importing @sentry/* types into lib/.
interface ScrubbableEvent {
  request?: {
    url?: string;
    query_string?: unknown;
    headers?: Record<string, string>;
  };
  breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
}

/**
 * Scrub URL-borne secrets from a Sentry event in place and return it.
 * Safe to pass any event shape; unknown fields are left untouched.
 */
export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
  const req = event.request;
  if (req) {
    if (req.url) req.url = scrubUrl(req.url);
    if (typeof req.query_string === 'string') {
      req.query_string = scrubQueryString(req.query_string);
    }
    if (req.headers?.Referer) req.headers.Referer = scrubUrl(req.headers.Referer);
  }
  for (const crumb of event.breadcrumbs ?? []) {
    const data = crumb.data;
    if (!data) continue;
    for (const key of ['url', 'to', 'from']) {
      const value = data[key];
      if (typeof value === 'string') data[key] = scrubUrl(value);
    }
  }
  return event;
}
